import { Suspense, useRef, useState, useEffect, createContext, useContext, useCallback } from "react";
import { PageLoader } from "@/components/ui/PageLoader";
import { useScrollResetOnRouteChange } from "@/hooks/useScrollResetOnRouteChange";
import { useGeoWarmup } from "@/hooks/useGeoWarmup";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { CalendarDays, CalendarRange, User, History, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import ultureLogo from "@/assets/ulture-logo-transparent.png";
import { PdfViewerProvider } from "@/contexts/PdfViewerContext";
import { PdfViewerOverlay } from "@/components/ui/PdfViewerOverlay";

interface NavItem {
  path: string;
  icon: React.ElementType;
  labelKey: string;
}

const leftNav: NavItem[] = [
  { path: "/worker/today", icon: CalendarDays, labelKey: "worker.nav.today" },
  { path: "/worker/upcoming", icon: CalendarRange, labelKey: "worker.nav.upcoming" },
];
const rightNav: NavItem[] = [
  // «Historial»: la pestaña lista lo que el trabajador ya ha reportado, así que
  // el ojo de «Muestreo» pasa a ser el icono de historial (flecha en círculo).
  { path: "/worker/scouting", icon: History, labelKey: "worker.nav.history" },
  { path: "/worker/profile", icon: User, labelKey: "worker.nav.profile" },
];

/* ------------------------------------------------------------------ */
/* WorkerDockContext — lets any child page hide the floating bottom    */
/* dock (e.g. the incident-report wizard so its sticky Continuar       */
/* button is never covered, especially on desktop).                    */
/* ------------------------------------------------------------------ */

interface WorkerDockCtx {
  hide: () => void;
  show: () => void;
}

const WorkerDockContext = createContext<WorkerDockCtx>({
  hide: () => {},
  show: () => {},
});

export function useHideWorkerDock() {
  const ctx = useContext(WorkerDockContext);
  useEffect(() => {
    ctx.hide();
    return () => ctx.show();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export function WorkerLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, company } = useAuth();
  const { t } = useTranslation();
  const mainRef = useRef<HTMLElement>(null);
  const [dockHiddenCount, setDockHiddenCount] = useState(0);

  useScrollResetOnRouteChange(() => [mainRef.current]);
  // Pide el permiso de ubicación al entrar al portal (diálogo nativo Despia o
  // prompt web) para que el fix esté caliente al abrir "Reportar plaga".
  useGeoWarmup();

  const ctx: WorkerDockCtx = {
    hide: useCallback(() => setDockHiddenCount((n) => n + 1), []),
    show: useCallback(() => setDockHiddenCount((n) => Math.max(0, n - 1)), []),
  };

  const dockHidden = dockHiddenCount > 0;

  const renderItem = (item: NavItem) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    return (
      <Link
        key={item.path}
        to={item.path}
        className={cn(
          "flex flex-col items-center justify-center gap-0.5 flex-1 h-full rounded-2xl transition-colors",
          isActive ? "text-primary" : "text-muted-foreground"
        )}
      >
        <Icon className={cn("w-5 h-5", isActive && "stroke-[2.5]")} />
        <span className={cn("text-[10px] font-medium leading-none", isActive && "font-semibold")}>
          {t(item.labelKey)}
        </span>
      </Link>
    );
  };

  const openReportPicker = () => {
    // Navigate to the scouting list which hosts the Foto/Voz picker; the
    // `?report=1` query opens it automatically.
    navigate("/worker/scouting?report=1");
  };

  return (
    <PdfViewerProvider>
      <WorkerDockContext.Provider value={ctx}>
        {/* Alto acotado al viewport (como el layout del responsable): así el <main>
            es el contenedor que DE VERDAD hace scroll. Con `min-h-screen` el alto
            crecía con el contenido, el scroll lo hacía la ventana y su
            `overflow-auto` dejaba a la cabecera `sticky` sin sitio donde pegarse.
            `dvh` sigue a la barra del navegador del móvil. */}
        <div className="h-[100dvh] flex flex-col overflow-hidden" style={{ background: "var(--bg-page)" }}>
          {/* No global header — each worker page renders its own
              WorkerPageHeader (tall gradient with page title). */}

          {/* Main content area with a real scroll-flow spacer for bottom nav */}
          {/* `min-h-0`: sin él, un hijo flex no encoge por debajo de su contenido y
              el overflow no llega a activarse nunca.
              `overscroll-none`: sin esto, al tirar hacia abajo el rebote elástico
              arrastra TODO el contenido —cabecera incluida— y se ve por encima de
              ella; así el desplazamiento se para en el tope y la cabecera no se
              mueve nunca. */}
          <main ref={mainRef} className="flex-1 min-h-0 overflow-auto overscroll-none flex flex-col dock-safe-scroll"
            style={{ paddingTop: 0 }}
          >
            <Suspense fallback={<PageLoader variant="list" />}>
              <Outlet />
            </Suspense>
          </main>


          {/* Bottom Navigation – manager-style dock with centered danger button */}
          {!dockHidden && (
            <div
              data-app-dock
              className="fixed left-0 right-0 z-[45] pointer-events-none"
              style={{ bottom: "var(--dock-bottom)" }}
            >
              <div className="mx-auto max-w-md px-[14px]">
                <div className="pointer-events-auto relative h-16 rounded-[24px] bg-white/[0.92] backdrop-blur-[16px] border border-[#E5E9F2] shadow-[0_12px_34px_rgba(20,60,100,0.16)] flex items-stretch px-2 md:px-3 md:gap-1.5">
                  {leftNav.map(renderItem)}
                  <div className="w-16 flex-shrink-0" aria-hidden />
                  {rightNav.map(renderItem)}

                  {/* Center danger / report-pest button */}
                  <button
                    onClick={openReportPicker}
                    aria-label={t("worker.incident.reportTitle")}
                    className="absolute left-1/2 -translate-x-1/2 -top-5 w-14 h-14 rounded-full bg-gradient-to-br from-red-500 to-orange-500 shadow-lg shadow-red-500/30 flex items-center justify-center active:scale-95 transition-transform"
                  >
                    <AlertTriangle className="w-6 h-6 text-white" strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            </div>
          )}

          <PdfViewerOverlay />
        </div>
      </WorkerDockContext.Provider>
    </PdfViewerProvider>
  );
}
